import { useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

type ReviewImageLightboxProps = {
  images: string[];
  /** `null` = closed. */
  index: number | null;
  onChange: (index: number) => void;
  onClose: () => void;
};

/**
 * Full-viewport review image lightbox shared by the main reviews section
 * (`ReviewCard`) and the store-page «آخر التقييمات» grid (`RecentReviewsGrid`).
 *
 * Rendered through a portal into `document.body`: a `position: fixed` overlay
 * resolves against its nearest *transformed* ancestor, not the viewport, so
 * rendering it inside a card that carries `transform: translateY(...)` clips it
 * to the card. Portalling detaches it from any transformed/overflowing ancestor.
 */
export default function ReviewImageLightbox({
  images,
  index,
  onChange,
  onClose,
}: ReviewImageLightboxProps) {
  // Keyboard navigation while the lightbox is open. The page is RTL, so the
  // LEFT arrow advances and the RIGHT arrow goes back (matches the on-screen
  // chevrons: next sits on the physical left, prev on the physical right).
  useEffect(() => {
    if (index === null || images.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        onChange((index + 1) % images.length);
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        onChange((index - 1 + images.length) % images.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, onChange, onClose]);

  // SSR-safe: no `document` on the server.
  if (typeof document === "undefined") return null;
  if (index === null || !images[index]) return null;

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "rgba(0,0,0,.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "zoom-out",
      }}
      onClick={onClose}
    >
      <img
        src={images[index]}
        alt=""
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "90vw",
          maxHeight: "90vh",
          borderRadius: "var(--ys-r-md)",
          objectFit: "contain",
          cursor: "default",
        }}
      />

      {images.length > 1 && (
        <>
          {/* Next (physical left) */}
          <button
            aria-label="الصورة التالية"
            onClick={(e) => {
              e.stopPropagation();
              onChange((index + 1) % images.length);
            }}
            style={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              width: 40,
              height: 40,
              borderRadius: "var(--ys-r-pill)",
              background: "rgba(255,255,255,.15)",
              border: "none",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <ChevronLeft style={{ width: 22, height: 22 }} />
          </button>

          {/* Prev (physical right) */}
          <button
            aria-label="الصورة السابقة"
            onClick={(e) => {
              e.stopPropagation();
              onChange((index - 1 + images.length) % images.length);
            }}
            style={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              width: 40,
              height: 40,
              borderRadius: "var(--ys-r-pill)",
              background: "rgba(255,255,255,.15)",
              border: "none",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <ChevronRight style={{ width: 22, height: 22 }} />
          </button>

          {/* Counter */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              direction: "ltr",
              fontFamily: "var(--ys-font)",
              fontSize: 13,
              color: "#fff",
              background: "rgba(0,0,0,.5)",
              borderRadius: "var(--ys-r-pill)",
              padding: "3px 12px",
            }}
          >
            {index + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}
